/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: unified
 *
 * Card de sucursal en el rail teal (patrón Square location picker).
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import type { BranchAccess } from '../session/store/useSessionStore';
import {
  getMergedActiveCashSessionId,
  subscribeCashSessionChangeSignalDebounced,
} from '../../cash/infra-api';

function formatBranchLocation(location?: string | null): string | null {
  const value = location?.trim();
  if (!value) return null;
  const lower = value.toLowerCase();
  if (lower === 'dirección no cargada' || lower === 'direccion no cargada') return null;
  return value;
}

type Props = {
  branch: BranchAccess;
  onPress: () => void;
};

export function SidebarBranchCard({ branch, onPress }: Props) {
  const [cashOpen, setCashOpen] = useState<boolean | null>(null);
  const location = formatBranchLocation(branch.location);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const sessionId = await getMergedActiveCashSessionId(branch.id);
        if (!cancelled) setCashOpen(!!sessionId);
      } catch {
        if (!cancelled) setCashOpen(null);
      }
    };
    void load();
    const unsubscribe = subscribeCashSessionChangeSignalDebounced(() => {
      void load();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [branch.id]);

  const metaParts = [
    location,
    cashOpen === null ? null : cashOpen ? 'Caja abierta' : 'Caja cerrada',
  ].filter(Boolean);

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Sucursal ${branch.name}. Cambiar de sucursal`}
      testID="auth.sidebar-branch-card"
    >
      <View style={styles.copy}>
        <Text style={styles.kicker}>Sucursal</Text>
        <View style={styles.nameRow}>
          <Text style={styles.name} numberOfLines={1}>
            {branch.name}
          </Text>
          <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.85)" />
        </View>
        {metaParts.length > 0 ? (
          <Text style={styles.meta} numberOfLines={2}>
            {metaParts.join(' · ')}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: spacing.sm,
    marginBottom: spacing.sm,
    marginTop: spacing.xs,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
  },
  copy: {
    gap: 2,
  },
  kicker: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.65)',
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  name: {
    ...typography.body,
    flex: 1,
    minWidth: 0,
    fontWeight: '800',
    color: '#fff',
  },
  meta: {
    ...typography.caption,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
    lineHeight: 16,
  },
});
